<template>
  <div>
    <Head title="Budget Amounts" />
    <h1 class="mb-8 text-3xl font-bold">Budget Amounts</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset" />
      <a
        v-if="user.group_permissions.includes('create_payment_requisition')
          || user.per_user_permissions.includes('create_payment_requisition')" 
        class="btn-orange" href="#" @click="openCreateNewPrWindow"
      >
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;New</span>
      </a>
    </div>
    <hr />

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-2">Date Created</th>
          <th class="pb-3 pt-3 px-2">Financial Year</th>
          <th class="pb-3 pt-3 px-2">Budget Line</th>
          <th class="pb-3 pt-3 px-2" colspan="2">Amount</th>
        </tr>
        <tr v-for="pr in budgetAmounts.data" :key="pr.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-2 py-2 focus:text-indigo-500" :href="`#/payment-requests/${pr.id}/payment-request-details`">
              {{ pr.date }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-2 py-2" :href="`#/payment-request/${pr.id}/payment-request-details`" tabindex="-1">
              {{ pr.financial_year }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-2 py-2" :href="`#/payment-request/${pr.id}/payment-request-details`" tabindex="-1">
              {{ pr.budget_line }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-2 py-2" :href="`#/payment-request/${pr.id}/payment-request-details`" tabindex="-1">
              {{ pr.amount }}
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-4" :href="`#/payment-request/${pr.id}/payment-request-details`" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="budgetAmounts.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="8">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="budgetAmounts.links" />
  </div>
  <div v-if="isCreatePrOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 24px;">New Payment Request</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              You are about to create new Payment Request, do you wish to continue?
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="openCreateNewPrWindow">
                Yes
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeOpenPR">
                No
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="isCreatePrWindowOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white rounded-lg text-left w-1/2 overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 24px;">New Budget Amount</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="flex flex-wrap -mb-8 -mr-6 p-8">
              <div class="pb-8 pr-6 w w-full lg:w-full">
                <p class="mb-2">Budget Lines:</p>
                <multiselect
                  id="budget"
                  v-model="selectedBudget"
                  placeholder="Search Budget Lines"
                  :options="budgets"
                  :close-on-select="true"
                  :searchable="true"
                  @search-change="onSearchBudgetsChange"
                  @input="onSelectedBudget"
                />
                <div v-if="$page.props.errors" class="form-error pb-1 pr-6 w-full lg:w-4/4 mb-2">
                  {{ $page.props.errors.budget }}
                </div>
              </div>
              <div class="pb-8 pr-6 w w-full lg:w-full">
                <p class="mb-2">Financial Year:</p>
                <multiselect
                  id="financialYear"
                  v-model="selectedFinancialYear"
                  placeholder="Search Financial year"
                  :options="financialYears"
                  :close-on-select="true"
                  :searchable="true"
                  @search-change="onSearchFinancialYearChange"
                  @input="onSelectedFinancialYear"
                />
                <div v-if="$page.props.errors" class="form-error pb-1 pr-6 w-full lg:w-4/4 mb-2">
                  {{ $page.props.errors.financialYear }}
                </div>
              </div>
              <div class="pb-8 pr-6 w w-full lg:w-full">
                <text-input v-model="formData.amount" class="pb-8 pr-4 w-full lg:w-full" label="Amount" />
                <div v-if="$page.props.errors" class="form-error pb-1 pr-6 w-full lg:w-4/4 mb-2">
                  {{ $page.props.errors.amount }}
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button
                v-if="user.group_permissions.includes('create_payment_requisition')
                  || user.per_user_permissions.includes('create_payment_requisition')
                  || user.per_user_permissions.includes('create_global_payment_requisition')"
                type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="submitNewPR"
              >
                Save
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeOpenPR">
                Cancel
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
    
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import Multiselect from '@vueform/multiselect'
import TextInput from '@/Shared/TextInput'
    
export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
    Multiselect,
    TextInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    budgetAmounts: Object,
    financialYears: Object,
    budgets: Object,
    errors: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
      },
      formData: {
        budget: null,
        financialYear: null,
        amount: null,
      },
      isCreatePrOpen: false,
      isCreatePrWindowOpen: false,
    }
  },
  computed: {
    user() {
      return this.$inertia.page.props.auth.user
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/budget-amounts', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    openCreateNewPr() {
      this.isCreatePrOpen = true
    },
    closeOpenPR() {
      this.isCreatePrOpen = false
      this.isCreatePrWindowOpen = false
    },
    openCreateNewPrWindow() {
      this.isCreatePrWindowOpen = true
    },
    submitNewPR() {
      this.$inertia.post('/budget-amounts', this.formData, {
        onSuccess: () => {
          this.isCreatePrOpen = false
          this.$router.push('/budget-amounts')
        },
      })
    },
    onSelectedBudget(budget, id) {
      this.formData.budget = budget
    },
    onSelectedFinancialYear(financialYear, id) {
      this.formData.financialYear = financialYear
    },
  },
}
</script>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  <style type="text/css">
    .multiselect-option.is-selected.is-pointed {
      background: var(--ms-option-bg-selected-pointed,#fb923c);
      color: var(--ms-option-color-selected-pointed,#fff);
    }
    .multiselect-option.is-selected {
      background: var(--ms-option-bg-selected-pointed,#fb923c);
      color: var(--ms-option-color-selected-pointed,#fff);
    }
  </style>
    